import { Temporal } from "@js-temporal/polyfill";
import * as SubscriptionType from "./type";
import { FetchError, WheeError } from "../error";
import { PromiseResult } from "../neverthrow-ext";
import { z } from "zod";
import { firstValueOrUndefined, transformToShallowObject } from "../zod-utils";
import AirtableApi from "../airtable/api";
import { BikeATStore, BikeModel, BikeStoreError, IBikeStore } from "../bike";
import { notEmpty, unique } from "..";
import { formulaGetAllIds } from "../airtable";
import { overrideName, overrideSalesPoints, overrideCondition } from "./seasonalOverride";

export { SubscriptionType };

export interface ISubscriptionStore {
  getAllSubscriptionProducts(): PromiseResult<SubscriptionProduct[], SubscriptionStoreError>;
  getSubscriptionProductsByBikeModelId(
    id: string
  ): PromiseResult<SubscriptionProductWithPrice[], SubscriptionStoreError | BikeStoreError>;
  getSubscriptions(ids: string[]): PromiseResult<Subscription[], SubscriptionStoreError>;
}

type SubscriptionStoreError = NoSubscriptionsForBikeType | FetchError;

class NoSubscriptionsForBikeType extends WheeError {
  public readonly bikeTypeId: string;

  constructor(bikeTypeId: string) {
    super(`No subscriptions for bike type: ${bikeTypeId}`);
    this.bikeTypeId = bikeTypeId;
  }
}

const ZSubscriptionProduct = z
  .object({
    id: z.string(),
    fields: z.object({
      name: z.string(),
      salesPoints: z
        .string()
        .transform((val) => val.split("\n"))
        .superRefine((arg, ctx): arg is [string, ...string[]] => {
          if (arg.length == 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Sales points list can't be empty"
            });
          }
          return z.NEVER;
        })
    })
  })
  .transform(transformToShallowObject);

export type SubscriptionProduct = z.infer<typeof ZSubscriptionProduct>;

// This big chunk ensures that ["salesPoint1\nsalesPoint2"] | undefined -> ["salesPoint1", "salesPoint2"] | []
const ZSubscriptionProductSalesPoints = z
  .array(z.string())
  .optional()
  .transform(firstValueOrUndefined)
  .transform((val) => {
    if (!val) {
      return [];
    }
    return val.split("\n");
  })
  // use type predicate to tell the type system that there will always be a string in the list: https://zod.dev/?id=type-refinements
  .superRefine((arg, ctx): arg is [string, ...string[]] => {
    if (arg.length == 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sales points list can't be empty"
      });
    }
    return z.NEVER;
  });

const ZSubscriptionCommon = z.object({
  price: z.number(),
  displayName: z.string(),
  archived: z.boolean().catch(false),
  canBePurchasedOnline: z.boolean().catch(false)
});
const ZSubscriptionBike = z
  .object({
    id: z.string(),
    fields: z
      .object({
        type: z.literal("Bike"),
        subscriptionProductId: z
          .array(z.string())
          .optional()
          .transform(firstValueOrUndefined)
          .superRefine((arg, ctx): arg is string => {
            if (!arg) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Subscription product id can't be empty"
              });
            }

            return z.NEVER;
          }),
        subscriptionProductSalesPoints: ZSubscriptionProductSalesPoints,
        subscriptionProductCondition: z
          .string()
          .array()
          .transform((val) => val[0])
          .catch("12 mnd binding"),
        stripePriceId: z.string()
      })
      .merge(ZSubscriptionCommon)
  })
  .transform(transformToShallowObject)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  .transform(({ type, ...rest }) => ({
    type: "bike" as const,
    ...rest
  }));

const ZSubscriptionAccessory = z
  .object({
    id: z.string(),
    fields: z
      .object({
        type: z.literal("Accessory")
      })
      .merge(ZSubscriptionCommon)
  })
  .transform(transformToShallowObject)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  .transform(({ type, ...rest }) => ({
    type: "accessory" as const,
    ...rest
  }));

export const ZSubscription = z
  .union([ZSubscriptionBike, ZSubscriptionAccessory])
  .transform(({ displayName, ...rest }) => ({
    name: displayName,
    ...rest
  }));

export type Subscription = z.infer<typeof ZSubscription>;

export type SubscriptionProductWithPrice = Pick<SubscriptionProduct, "name" | "salesPoints"> & {
  price: number;
  subscriptionProductId: string;
  subscriptionId: string;
};

// todo(thusanarul): replace this with the type Subscription. No reason to have this class.
export class CSubscription {
  constructor(
    public readonly description: string,
    public readonly name?: string,
    public readonly price?: number,
    public readonly createdAt?: Temporal.Instant,
    public readonly type?: SubscriptionType.Type,
    public readonly quantity: number = 1
  ) {}
}

export class SubscriptionATStore extends AirtableApi implements ISubscriptionStore {
  // airtable: https://airtable.com/appSjtNqzH36perly/tblS0wpXO5t8Etrui/viwkHcB6qvR1vvRzv?blocks=hide
  getAllSubscriptionProducts = (): PromiseResult<SubscriptionProduct[], SubscriptionStoreError> => {
    return this.getTableRecords("tblS0wpXO5t8Etrui", "", ZSubscriptionProduct);
  };

  // airtable: https://airtable.com/appSjtNqzH36perly/tbl2uXjhZlV3xZxfb/viwOugND95MnX8qFJ?blocks=hide
  getSubscriptionProductsByBikeModelId = (
    id: string
  ): PromiseResult<SubscriptionProductWithPrice[], SubscriptionStoreError | BikeStoreError> => {
    const bikeStore: IBikeStore = new BikeATStore();

    return bikeStore
      .getBikeModel(id)
      .andThen(
        (bikeModel): PromiseResult<SubscriptionProductWithPrice[], SubscriptionStoreError> => {
          if (!bikeModel.subscriptions) {
            throw new NoSubscriptionsForBikeType(id);
          }

          const ids = bikeModel.subscriptions;
          const formula = formulaGetAllIds(ids);

          return this.getTableRecords("tbl2uXjhZlV3xZxfb", formula, ZSubscription).map((subs) => {
            return subs
              .map((sub) => {
                if (sub.type === "accessory" || !sub.canBePurchasedOnline) {
                  return null;
                }
                return {
                  subscriptionId: sub.id,
                  // Seasonal sales-page override; keyed on the original Airtable displayName.
                  name: overrideName(sub.name),
                  salesPoints: overrideSalesPoints(sub.name, sub.subscriptionProductSalesPoints),
                  price: sub.price,
                  subscriptionProductId: sub.subscriptionProductId
                };
              })
              .filter(notEmpty);
          });
        }
      );
  };

  getSubscriptions = (ids: string[]): PromiseResult<Subscription[], SubscriptionStoreError> => {
    const formula = formulaGetAllIds(ids);
    return this.getTableRecords("tbl2uXjhZlV3xZxfb", formula, ZSubscription).map((subs) =>
      subs.map(applySeasonalSubscriptionOverride)
    );
  };
}

/**
 * Applies the seasonal sales-page override (name + binding text) to a subscription,
 * keyed on its original Airtable displayName. Only sales surfaces read these objects;
 * min-side reads the order snapshot instead.
 */
const applySeasonalSubscriptionOverride = (sub: Subscription): Subscription => {
  if (sub.type === "bike") {
    return {
      ...sub,
      name: overrideName(sub.name),
      subscriptionProductSalesPoints: overrideSalesPoints(
        sub.name,
        sub.subscriptionProductSalesPoints
      ),
      subscriptionProductCondition: overrideCondition(sub.name, sub.subscriptionProductCondition)
    };
  }

  return { ...sub, name: overrideName(sub.name) };
};

export async function getUniqueSubscriptionsForBikes(
  subscriptionStore: ISubscriptionStore,
  bikes: BikeModel[]
) {
  const subscriptionIds = unique(bikes.flatMap((bike) => bike.subscriptions).filter(notEmpty));

  const subscriptions = await subscriptionStore.getSubscriptions(subscriptionIds);

  return subscriptions;
}

type BikeSubscription = Extract<Subscription, { type: "bike" }>;

export function filterBikeSubscriptions(subs: Subscription[]): BikeSubscription[] {
  const filtered = subs.filter<BikeSubscription>(
    (sub): sub is BikeSubscription => sub.type === "bike"
  );
  return filtered;
}
